// Capa de acceso a datos de Obras. Encapsula la llamada al backend para que el
// componente no dependa de axios ni de la forma del endpoint. Reusa la instancia
// de API existente (utils/api.js): baseURL de public/config.json + Bearer token.
//
// Backend: GET /api/obras -> fichas calculadas por ObrasRepository (camelCase). El mapeo a
// modelo interno se hace en obras.jsx (buildApiObra), junto a sus helpers.
import { getApiInstance, initializeApi } from "../../../../utils/api";

// Devuelve las filas crudas de la ficha de obras desde el API, o lanza si falla.
export async function fetchObras() {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.get("/api/obras");
    return Array.isArray(response?.data) ? response.data : [];
}

// Ficha de una obra concreta.
export async function fetchObra(codObra) {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.get(`/api/obras/${codObra}`);
    return response?.data ?? null;
}

// Alta (codObra 0/ausente) o edición parcial (codObra > 0, rowVersion).
// En edición, omitir conserva; null borra un valor nullable. Devuelve la ficha completa.
export async function guardarObra(obra) {
    await initializeApi();
    const api = getApiInstance();
    const codObra = Number(obra?.codObra) || 0;
    const response = codObra > 0
        ? await api.put(`/api/obras/${codObra}`, obra)
        : await api.post("/api/obras", obra);
    return response?.data ?? null;
}

// Baja logica (activa=false + motivo) o reactivacion (activa=true).
export async function archivarObra(codObra, activa, motivo) {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.post(`/api/obras/${codObra}/archivar`, { activa, motivo: motivo || null });
    return response?.data ?? null;
}

// Usuarios habilitados como PM (rol 1) / PMI (rol 2) para los selectores.
export async function fetchResponsables() {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.get("/api/obras/responsables");
    return Array.isArray(response?.data) ? response.data : [];
}

// Historial de auditoría de una obra (filas ya listas para el timeline).
export async function fetchAuditoria(codObra) {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.get(`/api/obras/${codObra}/auditoria`);
    return Array.isArray(response?.data) ? response.data : [];
}

// ── Catalogos ────────────────────────────────────────────────────────────────
// Devuelve todos los valores de catalogo (filas { tipo, codCatalogo, valor, activo, orden }).
export async function fetchCatalogos() {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.get("/api/obras/catalogos");
    return Array.isArray(response?.data) ? response.data : [];
}

// Alta (codCatalogo 0) o renombrado de un valor. Devuelve { codCatalogo, tipo, valor }.
export async function guardarCatalogo(tipo, codCatalogo, valor, motivo) {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.post("/api/obras/catalogos", {
        tipo, codCatalogo: Number(codCatalogo) || 0, valor, motivo: motivo || null,
    });
    return response?.data ?? null;
}

// Activa o desactiva un valor de catalogo.
export async function cambiarEstadoCatalogo(codCatalogo, activo, motivo) {
    await initializeApi();
    const api = getApiInstance();
    const response = await api.post(`/api/obras/catalogos/${codCatalogo}/estado`, { activo, motivo: motivo || null });
    return response?.data ?? null;
}

// Borrado fisico (el backend rechaza si el valor esta en uso).
export async function eliminarCatalogo(codCatalogo, motivo) {
    await initializeApi();
    const api = getApiInstance();
    await api.delete(`/api/obras/catalogos/${codCatalogo}`, { data: { motivo: motivo || null } });
}


const geoPath = tipo => ({ pais: "paises", subdivision: "subdivisiones", ciudad: "ubicaciones", ubicacion: "ubicaciones" }[tipo]);
export async function fetchGeografia() {
    await initializeApi();
    const api = getApiInstance();
    const [paises, subdivisiones, ubicaciones] = await Promise.all([
        api.get("/api/obras/paises"), api.get("/api/obras/subdivisiones"), api.get("/api/obras/ubicaciones"),
    ]);
    const map = row => ({ ...row, value: row.valor, active: row.activo === true || row.activo === 1 });
    const subdivisions = subdivisiones.data.map(map);
    const locations = ubicaciones.data.map(row => ({ ...map(row), parent: row.pais, subdivision: row.subdivision }));
    // ciudad es un alias temporal del modelo interno del front; el nombre mostrado es Ubicación.
    return { pais: paises.data.map(map), subdivision: subdivisions, ciudad: locations };
}
export async function guardarGeografia(tipo, body, id = 0) {
    await initializeApi();
    const api = getApiInstance();
    const path = "/api/obras/" + geoPath(tipo);
    const response = id ? await api.put(path + "/" + id, body) : await api.post(path, body);
    return response.data;
}
export async function estadoGeografia(tipo, id, activo, motivo) {
    await initializeApi();
    return getApiInstance().post("/api/obras/" + geoPath(tipo) + "/" + id + "/estado", { activo, motivo });
}
// Borrado físico de un país/ubicación sin dependencias; el motivo queda en el historial.
export async function eliminarGeografia(tipo, id, motivo) {
    await initializeApi();
    return getApiInstance().delete("/api/obras/" + geoPath(tipo) + "/" + id, { data: { motivo: motivo || null } });
}

// ── Calendarios suscritos (Outlook / Apple Calendar) ────────────────────────
// Cada calendario guarda la foto de la Agenda (obras y tipos de hito) y un token secreto;
// la URL del calendario la arma calendarSubscription.js con la base del API.
export async function listarCalendarios() {
    await initializeApi();
    const response = await getApiInstance().get("/api/obras/calendario/enlaces");
    return Array.isArray(response?.data) ? response.data : [];
}
// Cuerpo: { nombre, tipos, obras } (lista fija) o { nombre, tipos, filtro } (ver calendarRequest).
export async function crearCalendario(body) {
    await initializeApi();
    const response = await getApiInstance().post("/api/obras/calendario/enlaces", body);
    return response?.data ?? null;
}
export async function borrarCalendario(codEnlace) {
    await initializeApi();
    await getApiInstance().delete(`/api/obras/calendario/enlaces/${codEnlace}`);
}
// Base del API (config.json): la URL del calendario apunta al API, no al front.
export async function apiBaseUrl() {
    await initializeApi();
    return getApiInstance().defaults?.baseURL || "";
}
